import React, { useState, FormEvent, ChangeEvent } from 'react';
import './AudioGenerator.css';

interface ApiResponse {
  result: {
    audio_data: string;
  };
}

const AudioGenerator: React.FC = () => {
  const [inputText, setInputText] = useState<string>(
    "Hi, I am calling from Ovum Medical. I'd like to verify benefits for our patient. Great to meet you Lisa, let me know what information you need to get started. Thanks!"
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('');
  const [audioLength, setAudioLength] = useState<number | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Reset status and set loading
    setStatus('Processing...');
    setIsLoading(true);
    setAudioLength(null);
    
    const startTime = Date.now();
    
    // API configuration - same as in the Python script
    const url = "https://api.cortex.cerebrium.ai/v4/p-151ed52d/sesame-voice-tts/generate_audio";
    const apiKey = "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJwcm9qZWN0SWQiOiJwLTE1MWVkNTJkIiwiaWF0IjoxNzQwOTM1ODY4LCJleHAiOjIwNTY1MTE4Njh9.Jw76GtRfxpTB2vk31tvffqqf3rHyV3UvdPFagzDeQUCYTDZcwatoEjjeh3rDRbWdW0fQj3vm8cXDKigttzMwubYnNBsQNRSICefzOyN3-xMaDMDIa1RrQlR-yn9lpWfy0A3qY2gwI1lFgX5gsO8UI8XdW2j-lQJbisB1Aed2yT7pVZnGTSnlULZQO9cI4F8SWRL_6mKvrpd0Hu4N-tjuML3MFMnw8iH6r9jRPHESxyqZa4e9fSssZqB2_K_BK29tNRtqraOn161NHtXUh-BcQaifjmy0w0eVSyENFKahrT5JSkDlDO6rIt7OIodC928z_9LcsJW4lN7Pl5WU6ss4Rw";

    try {
      // Make API request
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: inputText })
      });

      const endTime = Date.now();
      const timeTaken = (endTime - startTime) / 1000;

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${await response.text()}`);
      }

      const result = await response.json() as ApiResponse;
      setStatus(`Generated audio in ${timeTaken.toFixed(2)} seconds!`);

      // Convert base64 to audio file and download
      const audioData = result.result.audio_data;
      const blob = base64ToBlob(audioData, 'audio/wav');
      
      // Create audio element to get duration (optional)
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      
      audio.addEventListener('loadedmetadata', () => {
        setAudioLength(parseFloat(audio.duration.toFixed(2)));
      });
      
      // Trigger download
      downloadBlob(blob, 'generated_audio.wav');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setStatus(`Error: ${errorMessage}`);
      console.error('Error generating audio:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert base64 to Blob
  const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteArrays: Uint8Array[] = [];

    for (let i = 0; i < byteCharacters.length; i += 512) {
      const slice = byteCharacters.slice(i, i + 512);
      const byteNumbers = new Array(slice.length);
      
      for (let j = 0; j < slice.length; j++) {
        byteNumbers[j] = slice.charCodeAt(j);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: mimeType });
  };

  // Helper function to download blob
  const downloadBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="audio-generator">
      <h1 className="audio-generator-title">Text-to-Speech Generator</h1>
      
      <form onSubmit={handleSubmit} className="audio-generator-form">
        <div className="form-group">
          <label htmlFor="text-input" className="form-label">Enter text to convert to speech:</label>
          <textarea
            id="text-input"
            value={inputText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInputText(e.target.value)}
            rows={5}
            required
            className="form-textarea"
          />
        </div>
        
        <button 
          type="submit" 
          disabled={isLoading || !inputText.trim()}
          className={`form-button ${isLoading ? 'loading' : ''}`}
        >
          {isLoading ? 'Generating...' : 'Generate Audio'}
        </button>
      </form>
      
      {status && (
        <div className="status-container">
          <p className="status">{status}</p>
          {audioLength && <p className="audio-length">Audio length: {audioLength} seconds</p>}
        </div>
      )}
    </div>
  );
};

export default AudioGenerator;